import { FunctionComponent, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { getTeacherGroups, getUsers } from "../../../services/firebase";
import {
  contextGroupAtom,
  contextGroupsAtom,
} from "../../../state/atoms/group";
import {
  contextStudentsAtom,
  paginatedFilteredStudentsAtom,
} from "../../../state/atoms/student";
import { contextNotificationAtom } from "../../../state/atoms/notification";
import {
  AuthUser,
  ChatComponent,
  TeacherDashboardNavbarSection,
  DashboardNavbarTypes,
  TeacherSection,
} from "../../../types";
import Notification from "../../Notification";
import DashboardNavbar from "../DashboardNavbar";
import Groups from "./Groups";
import Students from "./Students";
import { isFetchingDataAtom } from "../../../state/atoms/ui";
import Chat from "../chat/Chat";

const TeacherDashboard: FunctionComponent<{
  teacher: AuthUser;
  teacherSection?: TeacherSection;
  dashboardNavbarSection?: TeacherDashboardNavbarSection;
  chatComponent?: ChatComponent;
}> = ({ teacher, teacherSection, dashboardNavbarSection, chatComponent }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { uid: teacherUid } = teacher;
  const setStudents = useSetRecoilState(contextStudentsAtom);

  const setPaginatedFilteredStudents = useSetRecoilState(
    paginatedFilteredStudentsAtom
  );
  const [groups, setGroups] = useRecoilState(contextGroupsAtom);
  const [group, setGroup] = useRecoilState(contextGroupAtom);
  const [notification, setNotification] = useRecoilState(
    contextNotificationAtom
  );
  const setIsFetchingData = useSetRecoilState(isFetchingDataAtom);
  const [contacts, setContacts] = useState<string[]>([]);

  const teacherDashboardNavbarData = [
    {
      sectionName: dashboardNavbarSection?.chat || "chat",
      img: (
        <svg
          width="43"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            style={{ stroke: "currentcolor" }}
            d="M20.8732 28.2296C26.4518 28.2296 29.2418 28.2296 30.974 26.4959C32.7078 24.7636 32.7078 21.9736 32.7078 16.395C32.7078 10.8165 32.7078 8.02648 30.974 6.29419C29.2418 4.56042 26.4518 4.56042 20.8732 4.56042H14.9559C9.37738 4.56042 6.58737 4.56042 4.85508 6.29419C3.12131 8.02648 3.12131 10.8165 3.12131 16.395C3.12131 21.9736 3.12131 24.7636 4.85508 26.4959C5.82108 27.4633 7.11549 27.8909 9.03861 28.0787"
            stroke="#655C97"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            style={{ stroke: "currentcolor" }}
            d="M20.8732 28.2296C19.0448 28.2296 17.0299 28.9693 15.1911 29.9234C12.2354 31.4575 10.7576 32.2253 10.0298 31.7356C9.30193 31.2474 9.4395 29.7311 9.71614 26.7L9.77827 26.0106"
            stroke="#655C97"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      path: "/my-personal-area/chat",
    },
    {
      sectionName: dashboardNavbarSection?.students || "students",
      img: (
        <svg
          width="37"
          height="37"
          viewBox="0 0 37 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            style={{ fill: "currentcolor" }}
            d="M30.6987 27.2663C30.0447 25.717 29.0955 24.3098 27.9042 23.123C26.7164 21.9327 25.3094 20.9838 23.7609 20.3285C23.747 20.3215 23.7332 20.3181 23.7193 20.3111C25.8793 18.7509 27.2835 16.2095 27.2835 13.3421C27.2835 8.59209 23.435 4.74353 18.685 4.74353C13.9349 4.74353 10.0864 8.59209 10.0864 13.3421C10.0864 16.2095 11.4906 18.7509 13.6506 20.3146C13.6368 20.3215 13.6229 20.325 13.609 20.3319C12.0557 20.9872 10.6619 21.9268 9.46576 23.1265C8.27551 24.3142 7.32653 25.7212 6.67122 27.2697C6.02744 28.7858 5.68024 30.4111 5.6484 32.0579C5.64748 32.0949 5.65397 32.1317 5.66749 32.1662C5.68102 32.2006 5.7013 32.232 5.72715 32.2585C5.753 32.285 5.78389 32.3061 5.818 32.3204C5.85212 32.3348 5.88876 32.3422 5.92578 32.3422H8.00608C8.15863 32.3422 8.27998 32.2209 8.28345 32.0718C8.35279 29.3951 9.42762 26.8883 11.3276 24.9883C13.2935 23.0224 15.9043 21.9407 18.685 21.9407C21.4656 21.9407 24.0764 23.0224 26.0423 24.9883C27.9423 26.8883 29.0171 29.3951 29.0865 32.0718C29.0899 32.2243 29.2113 32.3422 29.3638 32.3422H31.4441C31.4812 32.3422 31.5178 32.3348 31.5519 32.3204C31.586 32.3061 31.6169 32.285 31.6428 32.2585C31.6686 32.232 31.6889 32.2006 31.7024 32.1662C31.716 32.1317 31.7224 32.0949 31.7215 32.0579C31.6868 30.4006 31.3436 28.7884 30.6987 27.2663ZM18.685 19.3056C17.0935 19.3056 15.5957 18.685 14.4689 17.5582C13.3421 16.4314 12.7214 14.9335 12.7214 13.3421C12.7214 11.7507 13.3421 10.2529 14.4689 9.12603C15.5957 7.9992 17.0935 7.37858 18.685 7.37858C20.2764 7.37858 21.7742 7.9992 22.901 9.12603C24.0279 10.2529 24.6485 11.7507 24.6485 13.3421C24.6485 14.9335 24.0279 16.4314 22.901 17.5582C21.7742 18.685 20.2764 19.3056 18.685 19.3056Z"
            fill="white"
          />
        </svg>
      ),
      path: "/my-personal-area/students",
    },
    // {
    //   sectionName: dashboardNavbarSection?.groups || "groups",
    //   img: (
    //     <svg
    //       width="49"
    //       height="30"
    //       viewBox="0 0 46 36"
    //       fill="none"
    //       xmlns="http://www.w3.org/2000/svg"
    //     >
    //       <path
    //         style={{ fill: "currentcolor" }}
    //         d="M10.6734 11.3297C7.58575 11.3297 5.08486 8.84719 5.08486 5.78221C5.08486 2.71723 7.58575 0.234741 10.6734 0.234741C13.7611 0.234741 16.262 2.71723 16.262 5.78221C16.262 8.84719 13.7611 11.3297 10.6734 11.3297ZM36.3808 11.3297C33.2931 11.3297 30.7923 8.84719 30.7923 5.78221C30.7923 2.71723 33.2931 0.234741 36.3808 0.234741C39.4685 0.234741 41.9694 2.71723 41.9694 5.78221C41.9694 8.84719 39.4685 11.3297 36.3808 11.3297ZM0.614014 20.9476C0.614014 16.8633 3.95318 13.5487 8.06776 13.5487H11.0507C12.1614 13.5487 13.2162 13.7914 14.1663 14.2213C14.0755 14.7206 14.0336 15.2407 14.0336 15.7677C14.0336 18.4166 15.2072 20.7951 17.0584 22.4246H2.10197C1.28464 22.4246 0.614014 21.7589 0.614014 20.9476ZM28.9271 22.4246H28.8782C30.7364 20.7951 31.903 18.4166 31.903 15.7677C31.903 15.2407 31.8541 14.7275 31.7703 14.2213C32.7203 13.7844 33.7752 13.5487 34.8859 13.5487H37.8688C41.9834 13.5487 45.3225 16.8633 45.3225 20.9476C45.3225 21.7659 44.6519 22.4246 43.8346 22.4246H28.9271ZM29.6745 15.7677C29.6745 19.4429 26.6707 22.4246 22.9683 22.4246C19.2658 22.4246 16.262 19.4429 16.262 15.7677C16.262 12.0925 19.2658 9.1107 22.9683 9.1107C26.6707 9.1107 29.6745 12.0925 29.6745 15.7677ZM9.55572 33.8871C9.55572 28.7834 13.7262 24.6436 18.8677 24.6436H27.0689C32.2104 24.6436 36.3808 28.7834 36.3808 33.8871C36.3808 34.9064 35.5495 35.7386 34.5156 35.7386H11.4209C10.394 35.7386 9.55572 34.9134 9.55572 33.8871V33.8871Z"
    //         fill="#655C97"
    //       />
    //     </svg>
    //   ),
    //   path: "/my-personal-area/groups",
    // },
  ];

  useEffect(() => {
    if (pathname === "/my-personal-area") navigate("/my-personal-area/chat");
  }, [pathname, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setIsFetchingData(true);

      const usersFromDb = await getUsers({
        hideTestUsers: false,
      });

      const studentsFromDb =
        usersFromDb?.filter((user) => !user.isTeacher) || [];
      setStudents(studentsFromDb);

      setPaginatedFilteredStudents(studentsFromDb);
      const groupsFromDb = await getTeacherGroups(teacherUid);
      setGroups(groupsFromDb);
      setGroup(groupsFromDb[0]);

      if (usersFromDb) {
        setContacts(
          usersFromDb
            ?.map((user) => user?.uid)
            .filter((userId) => userId !== teacherUid)
        );
      }
      setIsFetchingData(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherUid]);

  useEffect(() => {
    if (!notification.isVisible) return;
    setNotification({ ...notification, isVisible: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, notification]);

  const onSelectGroup = async (gid: string) => {
    const selectedGroup = groups?.find((group) => group.gid === gid);
    if (selectedGroup) setGroup(selectedGroup);
  };

  if (!teacherSection) return <> </>;
  const { selectContextGroup, noGroupsToDisplay } = teacherSection;

  return (
    <div className="mt-50">
      <DashboardNavbar
        dashboardData={teacherDashboardNavbarData}
        navbarType={DashboardNavbarTypes.teacherDashboard}
      />
      <div className="p-4 rounded shadow-lg mb-3">
        {pathname === "/my-personal-area/groups" && (
          <div className="d-flex w-100 align-items-center group-select-container text-start gap-20">
            {groups?.length ? (
              <>
                <div className="text-nowrap">{selectContextGroup}</div>
                <select
                  className="form-control form-select justify-self-end"
                  name="groupSelect"
                  id="groupSelect"
                  value={group?.gid}
                  onChange={(ev) => {
                    onSelectGroup(ev.currentTarget.value);
                  }}
                >
                  {groups?.map(({ groupName, gid }) => {
                    return (
                      <option key={gid} value={gid}>
                        {groupName}
                      </option>
                    );
                  })}
                </select>
              </>
            ) : (
              <div>{noGroupsToDisplay}</div>
            )}
          </div>
        )}
        <Routes>
          <Route
            path="/students"
            element={
              <Students
                teacherUid={teacherUid}
                teacherSection={teacherSection}
              />
            }
          />
          <Route
            path="/groups"
            element={<Groups teacherSection={teacherSection} />}
          />
          <Route
            path="/chat"
            element={
              <Chat
                contactIds={contacts}
                userId={teacherUid}
                chatComponent={chatComponent}
              />
            }
          />
          <Route
            path="/chat/:uid"
            element={
              <Chat
                contactIds={contacts}
                userId={teacherUid}
                chatComponent={chatComponent}
              />
            }
          />
        </Routes>
        {notification && <Notification {...notification} />}
      </div>
    </div>
  );
};

export default TeacherDashboard;
